import type { QaItem, QaSession } from "./types";

const SESSION_KEY = "agentforge.qa.session.v1";

function newId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function newItemId(): string {
  return newId("box");
}

export function emptySession(): QaSession {
  return {
    id: newId("qa"),
    targetUrl: "http://127.0.0.1:5192",
    repoPath: "",
    items: [],
    tasks: [],
    running: false,
  };
}

export function loadSession(): QaSession {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return emptySession();
    const j = JSON.parse(raw) as QaSession;
    if (!j || !Array.isArray(j.items)) return emptySession();
    return {
      ...emptySession(),
      ...j,
      running: false,
      items: j.items.map(normalizeItem),
      tasks: Array.isArray(j.tasks) ? j.tasks : [],
    };
  } catch {
    return emptySession();
  }
}

export function saveSession(session: QaSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, running: false }));
  } catch {
    /* ignore quota */
  }
}

function normalizeItem(raw: Partial<QaItem> & { title?: string }): QaItem {
  return {
    id: typeof raw.id === "string" && raw.id ? raw.id : newItemId(),
    title: String(raw.title || "").trim() || "Untitled box",
    actions: Array.isArray(raw.actions) ? raw.actions : [],
    authoredBy: raw.authoredBy === "owner" ? "owner" : "grok",
    approved: Boolean(raw.approved),
    status: raw.status === "pass" ? "pass" : "pending",
    screenshotPath: raw.screenshotPath,
    lastFailAt: raw.lastFailAt,
    passAt: raw.passAt,
    retries: typeof raw.retries === "number" ? raw.retries : 0,
  };
}

export type ParsedChecklist = {
  items: QaItem[];
  targetUrl?: string;
  repoPath?: string;
};

/** Import Grok-authored checklist JSON (array or { items }). Approval stays off. */
export function parseGrokChecklist(text: string): ParsedChecklist {
  const data = JSON.parse(text) as unknown;
  const wrap = data && typeof data === "object" && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
  const list = Array.isArray(data)
    ? data
    : wrap && Array.isArray(wrap.items)
      ? wrap.items
      : null;
  if (!list) throw new Error("Checklist must be a JSON array (or { items: [] })");
  return {
    items: list.map((row) =>
      normalizeItem({
        ...(row as Partial<QaItem>),
        authoredBy: "grok",
        approved: false,
        status: "pending",
        screenshotPath: undefined,
        passAt: undefined,
        retries: 0,
      }),
    ),
    targetUrl: typeof wrap?.targetUrl === "string" ? wrap.targetUrl : undefined,
    repoPath: typeof wrap?.repoPath === "string" ? wrap.repoPath : undefined,
  };
}

export function exportChecklist(session: QaSession): string {
  return JSON.stringify(
    {
      targetUrl: session.targetUrl,
      repoPath: session.repoPath,
      items: session.items.map((i) => ({
        id: i.id,
        title: i.title,
        actions: i.actions,
        authoredBy: i.authoredBy,
        approved: i.approved,
      })),
    },
    null,
    2,
  );
}
